import React, { useRef } from 'react';
import { Swiper, SwiperSlide } from 'swiper/react';
import type { Swiper as SwiperInstance } from 'swiper';
import 'swiper/css';
import { HtmlContent } from '@/components/ui/html-content';
import { MaskImage } from '@/pages/reports/components/ui';
import Arrow from '@/assets/images/Arrow.svg?url';
import { createHighlightsBarScrollTracker } from '@/lib/reportHighlightsScroll';

interface Slide {
  description: string;
}

interface TodayCarouselProps {
  slides: Slide[];
}

export const TodayCarousel: React.FC<TodayCarouselProps> = ({
  slides = []
}) => {
  const trackerRef = useRef<ReturnType<typeof createHighlightsBarScrollTracker> | null>(null);

  const bindTracker = (swiper: SwiperInstance) => {
    const reportId = swiper.el
      .closest('[data-report-id]')
      ?.getAttribute('data-report-id')
      ?.trim();
    if (!reportId) {
      trackerRef.current = null;
      return;
    }
    trackerRef.current = createHighlightsBarScrollTracker(reportId, slides.length);
  };

  const handleMove = (swiper: SwiperInstance) => {
    if (swiper.isBeginning && swiper.isEnd) return;
    trackerRef.current?.onScrollStart();
  };

  if (slides.length === 0) {
    return null;
  }

  return (
    <div
      className="relative w-full py-12"
      style={{
        background: 'radial-gradient(70% 70% at 50% 50%, #F2FF00 0%, rgba(242, 255, 0, 0) 100%)'
      }}
    >
      <div className="container mx-auto mb-8">
        <div>
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-['Times_New_Roman'] text-black flex items-center gap-2">
            <span className="leading-none">Høje</span>
            <span className="inline-flex items-center justify-center gap-1 self-center" aria-hidden>
              <MaskImage src={Arrow} color="#000000" className="w-8 h-5 shrink-0" ariaHidden />
              <MaskImage src={Arrow} color="#000000" className="w-10 h-6 shrink-0" ariaHidden />
              <MaskImage src={Arrow} color="#000000" className="w-12 h-8 shrink-0" ariaHidden />
            </span>
          </h2>
        </div>
      </div>
      <div className="container mx-auto relative">
        <div className="relative overflow-hidden flex items-center">
          <Swiper
            spaceBetween={16}
            slidesPerView="auto"
            className="w-full [&_.swiper-wrapper]:py-4"
            data-report-highlights-carousel
            data-report-total-items-count={slides.length}
            onSwiper={bindTracker}
            onSliderMove={handleMove}
            onSlideChange={handleMove}
            onReachEnd={() => trackerRef.current?.onScrollComplete()}
          >
            {slides.map((slide, index) => (
              <SwiperSlide
                key={index}
                className="!w-[calc(100%-32px)] md:!w-[400px]"
                data-report-highlights-item
              >
                <div className="bg-white rounded-lg shadow-md p-6 md:p-8 border-b-4 border-[#F2FF00]">
                  <HtmlContent
                    html={slide.description}
                    className="text-gray-700 leading-relaxed text-base md:text-lg"
                    styleOptions={{ h1LineHeight: '40px' }}
                  />
                </div>
              </SwiperSlide>
            ))}
          </Swiper>
        </div>
      </div>
    </div>
  );
};
